import React, {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import * as dropin from 'braintree-web-drop-in';
import {
  BRAINTREE_TYPES,
  BUTTON_NAME,
  MANUALLY_ERRORS,
  PAYMENT_ERROR_MESSAGE,
} from '../../constants/constants';
import {resetError, setErrorManually} from '../../actions/error.action';
import {cardOnFile} from '../../actions/checkout.action';
import {authBraintreePayment} from '../../actions/checkout.action';
import {resetAllStatus} from '../../actions/status.action';
import {
  Wrapper,
  Head,
  Title,
  SubTitle,
  ButtonWrapper,
  LinkButton,
} from '../../styles/pages/pages';
import {
  DropInContent,
  LoadingWrapper,
  UpdateButtonWrapper,
  Info,
} from './CardOnFile.style';
import {getBraintreeInitObj} from '../../helpers/braintree.helper';
import Loading from '../../components/Loading/Loading';
import PaymentErrorModal from '../../components/PopupModal/PaymentErrorModal';
import {TheButton} from '../CheckoutPage/CheckoutPage.style';

export const CardOnFile = ({}) => {
  const dispatch = useDispatch();
  const dropinInstance = useRef(null);
  const dropinContainer = useRef(null);
  const [isLoading, setLoading] = useState(true);
  const [showBraintreeButton, setShowBraintreeButton] = useState(false);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [isReloading, setIsReloading] = useState(false);
  const [isPaymentSuccess, setPaymentSuccess] = useState(false);

  const {auth, checkout, status, errors} = useSelector(state => ({
    auth: state.auth,
    checkout: state.checkout,
    status: state.status,
    errors: state.errors,
  }));
  useEffect(() => {
    if (!auth) {
      return;
    }
    dispatch(cardOnFile());
  }, [auth]);

  useEffect(() => {
    if (!checkout) return;
    const {
      clientToken,
      amount,
      allowPaypal,
      allowApplePay,
      allowGooglePay,
    } = checkout;
    const dropinObj = getBraintreeInitObj(
      clientToken,
      amount,
      allowPaypal,
      allowApplePay,
      allowGooglePay,
      dropinContainer.current
    );
    setLoading(false);
    dropin.create(dropinObj, (error, instance) => {
      if (error || !instance) {
        return;
      }
      dropinInstance.current = instance;
      setShowBraintreeButton(true);
      try {
        dropinInstance.current.on('paymentOptionSelected', data => {
          if (
            data &&
            (data.paymentOption === BRAINTREE_TYPES.APPLE_PAY ||
              data.paymentOption === BRAINTREE_TYPES.GOOGLE_PAY)
          ) {
            setShowBraintreeButton(false);
          }
        });
        dropinInstance.current.on('noPaymentMethodRequestable', () => {
          setShowBraintreeButton(false);
        });
        dropinInstance.current.on('paymentMethodRequestable', event => {
          if (
            event &&
            event.paymentMethodIsSelected &&
            (event.type === BRAINTREE_TYPES.APPLE_PAY_CARD ||
              event.type === BRAINTREE_TYPES.ANDROID_PAY_CARD)
          ) {
            sendNonceToServer();
          } else if (!showBraintreeButton) {
            setShowBraintreeButton(true);
          }
        });
      } catch (e) {
        setShowBraintreeButton(false);
        handleBraintreeFailed(e);
      }
    });

    return () => {
      if (dropinInstance.current) {
        dropinInstance.current.teardown(() => {
          dropinInstance.current = null;
        });
      }
    };
  }, [checkout]);

  useEffect(() => {
    if (status?.paymentSuccess && !isPaymentSuccess) {
      dispatch(resetAllStatus());
      setPaymentSuccess(true);
    }
  }, [status]);

  useEffect(() => {
    if (errors?.braintree && isSubmittingPayment) {
      setIsSubmittingPayment(false);
    }
  }, [errors, isSubmittingPayment]);

  const sendNonceToServer = () => {
    const {threeDSecureParameters, orderId, responseUrl} = checkout;
    if (!dropinInstance.current) {
      handleBraintreeFailed({
        message: PAYMENT_ERROR_MESSAGE.GENERAL,
      });
      return;
    }
    dropinInstance.current.requestPaymentMethod(
      {
        threeDSecure: threeDSecureParameters,
      },
      (requestPaymentMethodErr, payload) => {
        if (requestPaymentMethodErr) {
          handleBraintreeFailed(requestPaymentMethodErr);
          return;
        }
        if (payload && payload.nonce) {
          setIsSubmittingPayment(true);
          const fromSaveCard = true;
          dispatch(
            authBraintreePayment(
              responseUrl,
              {
                nonce: payload.nonce,
                deviceData: payload.deviceData,
              },
              orderId,
              false,
              fromSaveCard
            )
          );
        } else {
          handleBraintreeFailed({
            message: PAYMENT_ERROR_MESSAGE.ANOTHER_METHOD,
          });
        }
      }
    );
  };
  const clearSelectedPaymentMethod = () => {
    if (dropinInstance.current?.clearSelectedPaymentMethod) {
      dropinInstance.current.clearSelectedPaymentMethod();
    }
  };
  const handleBraintreeFailed = errors => {
    clearSelectedPaymentMethod();
    dispatch(
      setErrorManually(MANUALLY_ERRORS.AUTHORIZE_BRAINTREE_PAYMENT_FAILED, [
        errors,
      ])
    );
  };
  const resetBraintreeErrors = () => {
    setIsReloading(true);
    dispatch(resetError());
    clearSelectedPaymentMethod();
    location.reload();
  };
  const renderBrainTreeButton = showBraintreeButton && (
    <TheButton id="submit-button" onClick={sendNonceToServer}>
      {BUTTON_NAME.UPDATE_NOW}
    </TheButton>
  );
  const renderBraintreeErrors = errors?.braintree?.length > 0 && (
    <PaymentErrorModal
      isBraintree={true}
      errors={errors.braintree}
      showModal={errors.braintree.length > 0}
      callback={resetBraintreeErrors}
    />
  );
  const renderLoading = (
    <LoadingWrapper>
      <Loading isLight={false} />
    </LoadingWrapper>
  );
  if ((isSubmittingPayment || isReloading) && !isPaymentSuccess) {
    return renderLoading;
  }
  return (
    <Wrapper>
      {renderBraintreeErrors}
      <Head>
        <Title>Card On File</Title>
        <ButtonWrapper>
          <LinkButton to="/account" $nomargin={'true'}>
            Back to settings
          </LinkButton>
        </ButtonWrapper>
      </Head>
      <DropInContent>
        <Info>
          Add or remove your saved payment cards and choose your default payment
          card for future purchases. <br /> <br /> When you add a card, we
          perform a £500 verification to confirm it's valid, but this is not a
          charge and no money is taken from your account.
        </Info>
        {isLoading && !isPaymentSuccess && renderLoading}
        {!isPaymentSuccess && (
          <>
            <div id="dropin-container" />
            <UpdateButtonWrapper>{renderBrainTreeButton}</UpdateButtonWrapper>
          </>
        )}
        {isPaymentSuccess && (
          <SubTitle>Saved payment card has been successfully updated</SubTitle>
        )}
      </DropInContent>
    </Wrapper>
  );
};
